import React, { useState, useRef, useEffect } from "react";
import { formatCurrencyNetto, parseSwissNumber, STATUS_CONFIG } from "./zahlungsplanUtils";

const ZahlungsplanCell = ({
  entry,
  entryCount = 0,
  cellTotal = 0,
  column,
  week,
  readOnly,
  onSave,
  onDelete,
  onDoubleClick,
  onContextMenu,
  onDragStart,
  onDragOver,
  onDrop,
  isDragOver,
  isEditRequested,
  onEditRequestHandled,
}) => {
  const [editing, setEditing] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  // Bearbeiten eines bestehenden Werts wird über das Kontextmenü ("Bearbeiten")
  // angestossen — der Wunsch aus dem Modal kommt als isEditRequested herein.
  useEffect(() => {
    if (isEditRequested && entry && !editing && !readOnly) {
      setEditing(true);
      setInputValue(String(entry.netto_betrag || entry.betrag || ""));
      onEditRequestHandled?.();
    }
  }, [isEditRequested, entry, editing, readOnly, onEditRequestHandled]);

  const statusKey = entry?.status || (column.type === "einnahmen" ? "expected" : "expecting");
  const statusStyle = entry ? STATUS_CONFIG[statusKey] || STATUS_CONFIG.expected : null;

  const handleClick = () => {
    if (readOnly) return;
    // Bestehende Werte nicht per Direktklick editieren (Schutz vor versehentlichen
    // Änderungen) — nur über Rechtsklick → "Bearbeiten". Leere Zellen: Klick erfasst.
    if (entry) return;
    setEditing(true);
    setInputValue("");
  };

  const handleSave = () => {
    setEditing(false);
    const trimmed = inputValue.trim();
    const amount = parseSwissNumber(trimmed);

    // Empty or zero on no entry: do nothing
    if ((trimmed === "" || isNaN(amount) || amount === 0) && !entry) return;

    // Empty or zero on existing entry: delete it
    if ((trimmed === "" || isNaN(amount) || amount === 0) && entry) {
      onDelete?.(entry);
      return;
    }

    onSave(column, week, amount, entry);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSave();
    } else if (e.key === "Escape") {
      setEditing(false);
    }
  };

  return (
    <div
      className={`zp-cell ${entry ? "has-entry" : "empty"} ${isDragOver ? "drag-over" : ""}`}
      style={
        entry && statusStyle
          ? { backgroundColor: statusStyle.color, color: statusStyle.textColor }
          : undefined
      }
      onClick={!editing ? handleClick : undefined}
      onDoubleClick={() => entry && !readOnly && onDoubleClick?.(column, week, entry)}
      onContextMenu={(e) => {
        if (entry && !readOnly) {
          e.preventDefault();
          onContextMenu?.(e, entry);
        }
      }}
      draggable={!!entry && !readOnly && !editing}
      onDragStart={(e) => entry && onDragStart?.(e, entry, column)}
      onDragOver={(e) => {
        if (!entry) {
          e.preventDefault();
          onDragOver?.(e, column, week);
        }
      }}
      onDrop={(e) => {
        if (!entry) {
          onDrop?.(e, column, week);
        }
      }}
      title={
        entry
          ? `${STATUS_CONFIG[statusKey]?.label || statusKey}${entry.invoice_reference ? ` | ${entry.invoice_reference}` : ""}${
              entryCount > 1
                ? ` | ${entryCount} Einträge in dieser Zelle (Beträge summiert; Bearbeiten/Status betreffen den ersten Eintrag)`
                : ""
            }${readOnly ? "" : " | Rechtsklick zum Bearbeiten"}`
          : undefined
      }
    >
      {editing ? (
        <input
          ref={inputRef}
          type="text"
          className="zp-cell-input"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
        />
      ) : entry ? (
        <span className="zp-cell-amount">
          {formatCurrencyNetto(
            entryCount > 1 ? cellTotal : entry.netto_betrag || entry.betrag || 0
          )}
          {entryCount > 1 && (
            <span className="zp-cell-multi-badge">+{entryCount - 1}</span>
          )}
        </span>
      ) : null}
    </div>
  );
};

export default React.memo(ZahlungsplanCell);
